// B-136 — the pure half of "Comité de seguimiento" (`ComiteSeguimiento.jsx`): what the client's
// monthly follow-up presentation shows, from `getCierreOperationReport`. The backend sums; this
// orders, compares and names. Money arrives as strings with two decimals.

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export const numero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** "septiembre 2026" for `2026-09`. */
export const nombreDeMes = (mes) => {
  if (!/^\d{4}-\d{2}$/.test(String(mes || ""))) return "";
  return `${MESES[Number(mes.slice(5)) - 1]} ${mes.slice(0, 4)}`;
};

/** "Sep" for `2026-09`. */
export const mesCorto = (mes) => MESES_CORTOS[Number(String(mes).slice(5, 7)) - 1] || "";

/** How much `actual` moved against `anterior`, as a fraction; null when there was nothing before. */
export const variacion = (actual, anterior) => {
  const a = numero(actual);
  const b = numero(anterior);
  if (!b) return null;
  return (a - b) / Math.abs(b);
};

/** "+7,7 %" / "-0,4 %" / "—". */
export const porcentaje = (fraccion) => {
  if (fraccion === null || fraccion === undefined || !Number.isFinite(fraccion)) return "—";
  const texto = Math.abs(fraccion * 100).toFixed(1).replace(".", ",");
  if (texto === "0,0") return "0,0 %";
  return `${fraccion > 0 ? "+" : "-"}${texto} %`;
};

/** The month's four headline figures, each with the month before. */
export function indicadoresDelMes(reporte) {
  const actual = reporte?.totals?.actual || {};
  const anterior = reporte?.totals?.anterior || {};
  const par = (clave) => ({ actual: numero(actual[clave]), anterior: numero(anterior[clave]), variacion: variacion(actual[clave], anterior[clave]) });
  return {
    tx: par("tx"),
    valor: par("valor"),
    excesos: { ...par("excesos"), valor: numero(actual.excesoValor) },
    reversos: { ...par("reversos"), valor: numero(actual.reversosValor) },
    bbva: { ...par("bbvaTx"), valor: numero(actual.bbvaValor) },
    provisiones: par("provisiones"),
    recolecciones: par("recolecciones"),
  };
}

/**
 * "Número de transacciones acumulado": one line per year, one point per month. Each point is
 * `{ mes: 1..12, etiqueta: "Ene", [año]: tx }`; `anios` lists the years drawn, oldest first.
 * `comparacion` is the chosen year against the one before over the months both have.
 */
export function transaccionesPorAnio(monthly, mes) {
  const anios = [...new Set((monthly || []).map((m) => m.month.slice(0, 4)))].sort();
  const puntos = MESES_CORTOS.map((etiqueta, i) => ({ mes: i + 1, etiqueta }));
  (monthly || []).forEach((m) => {
    puntos[Number(m.month.slice(5)) - 1][m.month.slice(0, 4)] = numero(m.tx);
  });
  const anio = String(mes || "").slice(0, 4);
  const previo = String(Number(anio) - 1);
  let comparacion = null;
  if (anios.includes(anio) && anios.includes(previo)) {
    const comunes = puntos.filter((p) => p[anio] !== undefined && p[previo] !== undefined);
    const a = comunes.reduce((s, p) => s + p[anio], 0);
    const b = comunes.reduce((s, p) => s + p[previo], 0);
    if (comunes.length && b) comparacion = { anio, previo, diferencia: a - b, variacion: (a - b) / b };
  }
  return { anios, puntos, comparacion };
}

/** "Tendencia volumen": the month's days, numbered. */
export const tendenciaDiaria = (daily) => (daily || []).map((d) => ({ dia: Number(d.day.slice(8)), fecha: d.day, tx: numero(d.tx), valor: numero(d.valor) }));

/**
 * "Volumen / Montos TX" per office: the month before and this one, the variation, sorted by the
 * month before the way the presentation lists them (then this month, then the code).
 */
export function oficinasPorVolumen(reporte, campo = "tx") {
  return (reporte?.offices || [])
    .map((o) => ({
      codigo: o.officeCode,
      oficina: o.officeName,
      region: o.region,
      anterior: numero(o.anterior?.[campo]),
      actual: numero(o.actual?.[campo]),
    }))
    .filter((o) => o.anterior || o.actual)
    .map((o) => ({ ...o, variacion: variacion(o.actual, o.anterior) }))
    .sort((a, b) => b.anterior - a.anterior || b.actual - a.actual || String(a.codigo).localeCompare(String(b.codigo)));
}

/**
 * A ranking of one month ("actual" or "anterior") by a count and its value: excesos, sobre el
 * límite, reversos. Offices with none are left out; ties go to the larger value.
 */
export function ranking(reporte, periodo, cantidad, valor, limite = 10) {
  const filas = (reporte?.offices || [])
    .map((o) => ({
      codigo: o.officeCode,
      oficina: o.officeName,
      region: o.region,
      cantidad: numero(o[periodo]?.[cantidad]),
      valor: numero(o[periodo]?.[valor]),
    }))
    .filter((o) => o.cantidad > 0)
    .sort((a, b) => b.cantidad - a.cantidad || b.valor - a.valor);
  const top = filas.slice(0, limite);
  return {
    filas: top,
    total: { cantidad: top.reduce((s, o) => s + o.cantidad, 0), valor: top.reduce((s, o) => s + o.valor, 0) },
    oficinas: filas.length,
  };
}

/** The last month the report has transactions in, to open on when the chosen one has none. */
export const ultimoMesConDatos = (reporte) => {
  const meses = (reporte?.monthly || []).filter((m) => numero(m.tx) > 0).map((m) => m.month).sort();
  return meses.length ? meses[meses.length - 1] : null;
};

/** The Excel the committee takes away: per office, the excesos, over the limit and reversals. */
export function hojasDelComite(reporte, filtrar = (filas) => filas) {
  const titulo = `Comité de seguimiento · ${etiquetaDePeriodo(reporte)}`;
  const ant = etiquetaDePeriodo(reporte, "anterior");
  const act = etiquetaDePeriodo(reporte);
  const oficinas = filtrar(reporte.offices || []);
  const pct = (f) => (f === null ? null : f);
  const tope = Math.round((numero(reporte.overLimitFactor) - 1) * 100);
  return [
    {
      nombre: "Por oficina",
      titulo,
      subtitulo: `Transacciones y montos de ${ant} y ${act}`,
      columnas: [
        { titulo: "Código", ancho: 9 },
        { titulo: "Oficina", ancho: 30 },
        { titulo: "Regional", ancho: 16 },
        { titulo: `TX ${ant}`, formato: "count", ancho: 13 },
        { titulo: `TX ${act}`, formato: "count", ancho: 13 },
        { titulo: "Variación TX", formato: "percent", ancho: 12 },
        { titulo: `Montos ${ant}`, formato: "money", ancho: 18 },
        { titulo: `Montos ${act}`, formato: "money", ancho: 18 },
        { titulo: "Variación montos", formato: "percent", ancho: 14 },
        { titulo: "Recaudos BBVA", formato: "money", ancho: 16 },
        { titulo: "Provisiones", formato: "money", ancho: 16 },
        { titulo: "Recolecciones", formato: "money", ancho: 16 },
      ],
      filas: oficinas.map((o) => [
        o.officeCode,
        o.officeName,
        o.region || "",
        numero(o.anterior.tx),
        numero(o.actual.tx),
        pct(variacion(o.actual.tx, o.anterior.tx)),
        numero(o.anterior.valor),
        numero(o.actual.valor),
        pct(variacion(o.actual.valor, o.anterior.valor)),
        numero(o.actual.bbvaValor),
        numero(o.actual.provisiones),
        numero(o.actual.recolecciones),
      ]),
    },
    {
      nombre: "Excesos",
      titulo,
      subtitulo: `Días en exceso de cupo y sobre el ${tope} % del cupo, ${act}`,
      columnas: [
        { titulo: "Código", ancho: 9 },
        { titulo: "Oficina", ancho: 30 },
        { titulo: "Regional", ancho: 16 },
        { titulo: "Días en exceso", formato: "count", ancho: 13 },
        { titulo: "Valor del exceso", formato: "money", ancho: 18 },
        { titulo: `Días sobre el ${tope} %`, formato: "count", ancho: 14 },
        { titulo: `Valor sobre el ${tope} %`, formato: "money", ancho: 18 },
      ],
      filas: oficinas
        .filter((o) => numero(o.actual.excesos))
        .sort((a, b) => numero(b.actual.excesos) - numero(a.actual.excesos))
        .map((o) => [o.officeCode, o.officeName, o.region || "", numero(o.actual.excesos), numero(o.actual.excesoValor),
          numero(o.actual.sobreLimite), numero(o.actual.sobreLimiteValor)]),
    },
    {
      nombre: "Reversos",
      titulo,
      subtitulo: `Reversos por oficina, ${act}`,
      columnas: [
        { titulo: "Código", ancho: 9 },
        { titulo: "Oficina", ancho: 30 },
        { titulo: "Regional", ancho: 16 },
        { titulo: "Reversos", formato: "count", ancho: 11 },
        { titulo: "Valor", formato: "money", ancho: 18 },
      ],
      filas: oficinas
        .filter((o) => numero(o.actual.reversos))
        .sort((a, b) => numero(b.actual.reversos) - numero(a.actual.reversos))
        .map((o) => [o.officeCode, o.officeName, o.region || "", numero(o.actual.reversos), numero(o.actual.reversosValor)]),
    },
  ];
}

// --- Charts ------------------------------------------------------------------------------------

/** The month's days for the charts: volume, amounts, recaudos/pagos, excesses, cash in transit. */
export const diasDelMes = (daily) => (daily || []).map((d) => ({
  dia: Number(d.day.slice(8)),
  fecha: d.day,
  tx: numero(d.tx),
  valor: numero(d.valor),
  recaudos: numero(d.recaudos),
  pagos: numero(d.pagos),
  excesos: numero(d.excesos),
  excesoValor: numero(d.excesoValor),
  sobreLimite: numero(d.sobreLimite),
  provisiones: numero(d.provisiones),
  recolecciones: numero(d.recolecciones),
  reversos: numero(d.reversos),
}));

/** The month by regional: volume, amounts, excess days and reversals, largest volume first. */
export function porRegional(reporte) {
  const grupos = new Map();
  (reporte?.offices || []).forEach((o) => {
    const region = o.region || "Sin regional";
    const g = grupos.get(region) || { region, oficinas: 0, tx: 0, valor: 0, excesos: 0, reversos: 0 };
    g.oficinas += 1;
    g.tx += numero(o.actual?.tx);
    g.valor += numero(o.actual?.valor);
    g.excesos += numero(o.actual?.excesos);
    g.reversos += numero(o.actual?.reversos);
    grupos.set(region, g);
  });
  return [...grupos.values()].sort((a, b) => b.tx - a.tx);
}

/** The ten offices with most of `campo` this month, with the month before beside it, for grouped bars. */
export const topOficinas = (reporte, campo = "tx", limite = 10) => oficinasPorVolumen(reporte, campo)
  .sort((a, b) => b.actual - a.actual)
  .slice(0, limite);

/** Recaudos against pagos of the month, as the slices of a donut. */
export const recaudosYPagos = (reporte) => {
  const a = reporte?.totals?.actual || {};
  return [
    { nombre: "Recaudos", valor: numero(a.recaudos), cantidad: numero(a.recaudosTx) },
    { nombre: "Pagos", valor: numero(a.pagos), cantidad: numero(a.pagosTx) },
  ];
};

// --- The metrics workbook ----------------------------------------------------------------------

const fechaExcel = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/**
 * The metrics workbook the client builds its dashboards on (DashGerencial.xlsx): its INFO sheet, one
 * row per office and day with the same headings, and the committee's sheets after it. INFO's
 * "Modelo Oficina" and "Modalidad TDV" are Bancamía's own classification and are not in Bantotal.
 */
export function hojasDeMetricas(reporte, filtrar = (filas) => filas) {
  const permitidas = new Set(filtrar(reporte.offices || []).map((o) => o.officeCode));
  const base = (reporte.base || []).filter((b) => permitidas.has(b.officeCode));
  const tope = Math.round(numero(reporte.overLimitFactor) * 100);
  const info = {
    nombre: "INFO",
    titulo: `Base de métricas · ${etiquetaDePeriodo(reporte)}`,
    subtitulo: "Una fila por oficina y día, leída de los reportes de Bantotal de cada cierre",
    columnas: [
      { titulo: "FECHA RECAUDO", formato: "date", ancho: 13 },
      { titulo: "COD OFICINA", ancho: 11 },
      { titulo: "OFICINA", ancho: 32 },
      { titulo: "REGIONAL", ancho: 15 },
      { titulo: "Cupo Actual", formato: "money", ancho: 17 },
      { titulo: "Saldo Boveda", formato: "money", ancho: 17 },
      { titulo: `# > ${tope}% excesos`, formato: "count", ancho: 12 },
      { titulo: `valor > ${tope}% excesos`, formato: "money", ancho: 17 },
      { titulo: "número de provisiones", formato: "count", ancho: 12 },
      { titulo: "valor provisiones", formato: "money", ancho: 17 },
      { titulo: "número de recolecciones", formato: "count", ancho: 12 },
      { titulo: "valor recolecciones", formato: "money", ancho: 17 },
      { titulo: "número de Pagos", formato: "count", ancho: 12 },
      { titulo: "Pagos Caja", formato: "money", ancho: 17 },
      { titulo: "número de Recaudos", formato: "count", ancho: 12 },
      { titulo: "Recaudos de Caja", formato: "money", ancho: 17 },
      { titulo: "número de Recaudos BBVA", formato: "count", ancho: 12 },
      { titulo: "Recaudos BBVA", formato: "money", ancho: 17 },
    ],
    filas: base.map((b) => [
      fechaExcel(b.day),
      b.officeCode,
      b.officeName,
      b.region || "",
      b.cupo === null ? null : numero(b.cupo),
      b.saldo === null ? null : numero(b.saldo),
      numero(b.sobreLimite),
      b.margenSobreLimite === null ? null : numero(b.margenSobreLimite),
      numero(b.provisionesTx),
      numero(b.provisiones),
      numero(b.recoleccionesTx),
      numero(b.recolecciones),
      numero(b.pagosTx),
      numero(b.pagos),
      numero(b.recaudosTx),
      numero(b.recaudos),
      numero(b.bbvaTx),
      numero(b.bbvaValor),
    ]),
  };
  const diario = {
    nombre: "Resumen diario",
    titulo: `Resumen diario · ${etiquetaDePeriodo(reporte)}`,
    columnas: [
      { titulo: "Fecha", formato: "date", ancho: 12 },
      { titulo: "Oficinas", formato: "count", ancho: 10 },
      { titulo: "Transacciones", formato: "count", ancho: 14 },
      { titulo: "Montos", formato: "money", ancho: 19 },
      { titulo: "Recaudos", formato: "money", ancho: 19 },
      { titulo: "Pagos", formato: "money", ancho: 19 },
      { titulo: "Oficinas en exceso", formato: "count", ancho: 12 },
      { titulo: "Valor del exceso", formato: "money", ancho: 18 },
      { titulo: "Provisiones", formato: "money", ancho: 18 },
      { titulo: "Recolecciones", formato: "money", ancho: 18 },
      { titulo: "Reversos", formato: "count", ancho: 10 },
    ],
    filas: (reporte.daily || []).map((d) => [
      fechaExcel(d.day), numero(d.oficinas), numero(d.tx), numero(d.valor), numero(d.recaudos), numero(d.pagos),
      numero(d.excesos), numero(d.excesoValor), numero(d.provisiones), numero(d.recolecciones), numero(d.reversos),
    ]),
  };
  const [porOficina, excesos, reversos] = hojasDelComite(reporte, filtrar);
  return [info, diario, porOficina, excesos, excesosPorDia(reporte, base), reversos, detalleDeReversos(reporte, permitidas)];
}

/** The office-days of `base` that had some of `campo`, by date and then by office. */
const diasCon = (base, campo) => base
  .filter((b) => numero(b[campo]))
  .sort((a, b) => a.day.localeCompare(b.day) || String(a.officeCode).localeCompare(String(b.officeCode), "es", { numeric: true }));

/** Each day an office was over its cupo, with the date: the committee's "Excesos" sheet, office by day. */
function excesosPorDia(reporte, base) {
  const tope = Math.round((numero(reporte.overLimitFactor) - 1) * 100);
  return {
    nombre: "Excesos por día",
    titulo: `Comité de seguimiento · ${etiquetaDePeriodo(reporte)}`,
    subtitulo: `Cada día en exceso de cupo y sobre el ${tope} % del cupo, ${etiquetaDePeriodo(reporte)}`,
    columnas: [
      { titulo: "Fecha", formato: "date", ancho: 12 },
      { titulo: "Código", ancho: 9 },
      { titulo: "Oficina", ancho: 30 },
      { titulo: "Regional", ancho: 16 },
      { titulo: "En exceso", formato: "count", ancho: 11 },
      { titulo: "Valor del exceso", formato: "money", ancho: 18 },
      { titulo: `Sobre el ${tope} %`, formato: "count", ancho: 12 },
      { titulo: `Valor sobre el ${tope} %`, formato: "money", ancho: 18 },
    ],
    filas: diasCon(base, "excesos").map((b) => [fechaExcel(b.day), b.officeCode, b.officeName, b.region || "",
      numero(b.excesos), numero(b.excesoValor), numero(b.sobreLimite), numero(b.sobreLimiteValor)]),
  };
}

/**
 * B-163 — every reversal of the period, one row per transaction, with the columns of the report
 * Brink's reviewed in the session (its "Reversos.xlsx"), in its order. TIPIFICACION, JUSTIFICACION
 * and RESPONSABLE are theirs to fill and go empty. Only the offices the export reaches.
 */
function detalleDeReversos(reporte, permitidas) {
  return {
    nombre: "Detalle de reversos",
    titulo: `Comité de seguimiento · ${etiquetaDePeriodo(reporte)}`,
    subtitulo: `Cada reverso, ${etiquetaDePeriodo(reporte)}`,
    columnas: [
      { titulo: "CODIGO OFICINA", ancho: 10 },
      { titulo: "OFICINA", ancho: 30 },
      { titulo: "MÓDULO", formato: "count", ancho: 9 },
      { titulo: "TRANSACCIÓN", formato: "count", ancho: 12 },
      { titulo: "DESCRIPCIÓN TX", ancho: 30 },
      { titulo: "ESTADO TX", ancho: 9 },
      { titulo: "FECHA", formato: "date", ancho: 12 },
      { titulo: "HORA", ancho: 10 },
      { titulo: "MONTO", formato: "money", ancho: 16 },
      { titulo: "USUARIO", ancho: 14 },
      { titulo: "DÉBITO/CRÉDITO", ancho: 14 },
      { titulo: "ANULADA/CONTABILIZADA", ancho: 16 },
      { titulo: "CUENTA CLIENTE", ancho: 14 },
      { titulo: "TIPIFICACION", ancho: 14 },
      { titulo: "JUSTIFICACION", ancho: 16 },
      { titulo: "RESPONSABLE", ancho: 14 },
      { titulo: "Regional", ancho: 14 },
      { titulo: "Mes", ancho: 11 },
      { titulo: "Dia", formato: "count", ancho: 6 },
    ],
    filas: (reporte.reversals || []).filter((r) => permitidas.has(r.officeCode)).map((r) => [
      r.officeCode, r.officeName, r.modulo, r.transaccion, r.descripcion || "", r.estadoTx || "",
      fechaExcel(r.day), r.hora || "", r.monto === null ? null : numero(r.monto), r.usuario || "",
      r.debitoCredito || "", r.estado || "", r.cuentaCliente || "", "", "", "",
      r.region || "", nombreDeMes(r.day.slice(0, 7)).split(" ")[0], Number(r.day.slice(8)),
    ]),
  };
}

// --- The period ----------------------------------------------------------------------------------

const dmy = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : "");

/**
 * How the report's period and the one it is compared with are said: "octubre 2026" for a whole
 * month, "08-10-2026 a 14-10-2026" for any other range. `corta` gives a chip's word ("octubre", or
 * "Periodo" / "Anterior" for a range).
 */
export function etiquetaDePeriodo(reporte, cual = "actual", { corta = false } = {}) {
  const actual = cual === "actual";
  if (reporte?.wholeMonth !== false) {
    const mes = actual ? reporte?.month : reporte?.previousMonth;
    return corta ? nombreDeMes(mes).split(" ")[0] : nombreDeMes(mes);
  }
  if (corta) return actual ? "Periodo" : "Anterior";
  return actual ? `${dmy(reporte.from)} a ${dmy(reporte.to)}` : `${dmy(reporte.previousFrom)} a ${dmy(reporte.previousTo)}`;
}

/** The request for a period: a month (`YYYY-MM`) or a range `{ desde, hasta }`, and the offices chosen. */
export function pedidoDelComite(periodo, oficinas) {
  const pedido = typeof periodo === "string" ? { month: periodo } : { from: periodo.desde, to: periodo.hasta };
  if (oficinas && oficinas.length) pedido.offices = oficinas;
  return pedido;
}
